import { useState } from "react";
import { Mic2, ChevronRight, Shield, BookOpenCheck } from "lucide-react";
import { http, errMsg } from "../api";
import DobInput from "../components/DobInput";

export default function Login({ onAuth, adminMode: adminRoute = false, creatorMode: creatorRoute = false }) {
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState(adminRoute ? "admin" : creatorRoute ? "creator" : "client");
  const adminMode = role === "admin" && mode === "login";
  const creatorMode = role === "creator" && mode === "login";
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/creator";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async e => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const url = adminMode ? "/auth/admin-login" : mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : { ...form, role };
      const { data } = await http.post(url, payload);
      onAuth(data);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <main className="auth-shell">
      <section className="auth-visual">
        <div className="brand"><span className="brand-mark"><Mic2 size={18} /></span> vocalhouse</div>
        <div className="visual-copy">
          <span className="eyebrow">MANAGED VOICE STUDIO</span>
          <h1>Find the right<br /><em>voice</em> for your project.</h1>
          <p>A curated marketplace of professional voice artists across India. Discover, listen, hire — managed end-to-end.</p>
        </div>
        <div className="visual-stats">
          <div><strong>23+</strong><span>Languages</span></div>
          <div><strong>30+</strong><span>Use cases</span></div>
          <div><strong>1200+</strong><span>Approved samples</span></div>
        </div>
      </section>
      <section className="auth-form">
        <div className="form-wrap">
          <div className="mobile-brand brand"><span className="brand-mark"><Mic2 size={18} /></span> vocalhouse</div>
          <div className="form-header">
            <span className="eyebrow">{adminMode ? "OWNER CONSOLE" : creatorMode ? "AI AUDIOBOOK CREATOR HIRING" : mode === "login" ? "WELCOME BACK" : "JOIN VOCALHOUSE"}</span>
            <h2>{adminMode ? <><Shield size={20} style={{ display: "inline", marginRight: 6, color: "var(--primary)" }} />Admin sign in</> : creatorMode ? <><BookOpenCheck size={20} style={{ display: "inline", marginRight: 6, color: "var(--primary)" }} />Creator sign in / sign up</> : mode === "login" ? "Sign in to your workroom" : role === "artist" ? "Create your artist profile" : "Start hiring voices"}</h2>
            <p>{adminMode ? "Restricted access. Only authorised admin emails can sign in here." : creatorMode ? "One Google click creates your creator account. Then complete your profile, read the role and clear two short tests." : mode === "login" ? "Continue where your last project left off." : role === "artist" ? "Get discovered by production teams across India." : "Browse curated voices and hire in minutes."}</p>
          </div>
          {mode === "login" && (
            <div className="role-tabs four" data-testid="login-role-tabs">
              <button type="button" data-testid="login-client-role" className={role === "client" ? "selected" : ""} onClick={() => setRole("client")}>Login as Client</button>
              <button type="button" data-testid="login-artist-role" className={role === "artist" ? "selected" : ""} onClick={() => setRole("artist")}>Login as Voice Artist</button>
              <button type="button" data-testid="login-creator-role" className={role === "creator" ? "selected" : ""} onClick={() => setRole("creator")}>Login as AI Audiobook Creator</button>
              <button type="button" data-testid="login-admin-role" className={role === "admin" ? "selected" : ""} onClick={() => setRole("admin")}><Shield size={13} style={{ display: "inline", marginRight: 4 }} />Login as Admin</button>
            </div>
          )}
          {mode === "register" && (
            <div className="role-tabs">
              <button type="button" data-testid="register-client-role" className={role === "client" ? "selected" : ""} onClick={() => setRole("client")}>I&#39;m a Client</button>
              <button type="button" data-testid="register-artist-role" className={role === "artist" ? "selected" : ""} onClick={() => setRole("artist")}>I&#39;m a Voice Artist</button>
            </div>
          )}
          {creatorMode ? (
            <div className="creator-auth" data-testid="creator-google-panel">
              <button type="button" className="google-btn" onClick={googleSignIn} data-testid="creator-google-signin">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                Continue with Google — sign in or sign up
              </button>
              <ol className="creator-steps" data-testid="creator-steps-preview">
                <li><strong>Complete your profile</strong><span>Phone number, resume, English + one regional language</span></li>
                <li><strong>Understand the job role</strong><span>Read what an AI Audiobook Creator does</span></li>
                <li><strong>Language &amp; comprehension test</strong><span>Timed multiple-choice, one attempt</span></li>
                <li><strong>Listening accuracy test</strong><span>Mark the dialogue numbers from a shared Drive folder</span></li>
              </ol>
              <small className="muted">New here? The same Google button creates your creator account — no password needed. Your resume stays private to you and the Vocalhouse team.</small>
            </div>
          ) : (
          <form onSubmit={submit} data-testid={adminMode ? "admin-login-form" : "auth-form"}>
            {mode === "register" && (
              <div>
                <label>Full name</label>
                <input data-testid="auth-name-input" required onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" />
              </div>
            )}
            <div>
              <label>Email address</label>
              <input data-testid="auth-email-input" type="email" required onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div>
              <label>Password</label>
              <input data-testid="auth-password-input" type="password" required minLength={8} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" />
            </div>
            {mode === "register" && role === "artist" && (
              <>
                <div className="field-row">
                  <div>
                    <label>Date of birth (dd/mm/yyyy)</label>
                    <DobInput testid="artist-dob-input" value={form.dob} onChange={v => setForm({ ...form, dob: v })} />
                  </div>
                  <div>
                    <label>Gender</label>
                    <select data-testid="artist-gender-input" required onChange={e => setForm({ ...form, gender: e.target.value })}>
                      <option value="">Select</option>
                      <option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
                    </select>
                  </div>
                </div>
                <div className="field-row">
                  <div>
                    <label>Phone</label>
                    <input data-testid="artist-phone-input" onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91..." />
                  </div>
                  <div>
                    <label>City</label>
                    <input data-testid="artist-city-input" onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Mumbai" />
                  </div>
                </div>
              </>
            )}
            {mode === "register" && role === "client" && (
              <div className="field-row">
                <div>
                  <label>Company (optional)</label>
                  <input onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Company name" />
                </div>
                <div>
                  <label>Phone (optional)</label>
                  <input onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91..." />
                </div>
              </div>
            )}
            {error && <div data-testid="auth-error" className="error">{error}</div>}
            <button data-testid="auth-submit-button" className="primary wide" type="submit" disabled={busy}>
              {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}<ChevronRight size={16} />
            </button>
          </form>
          )}
          {!adminMode && !creatorMode && <div className="form-foot">
            {mode === "login" ? <>New to vocalhouse? <button data-testid="show-register-button" onClick={() => { setMode("register"); if (role === "admin") setRole("client"); }}>Create an account</button></> : <>Already have an account? <button data-testid="show-login-button" onClick={() => setMode("login")}>Sign in</button></>}
          </div>}
          {!adminMode && mode === "register" && role === "artist" && <div className="form-foot">Prefer the quick form? <a href="/apply" data-testid="goto-apply">Apply with voice samples</a></div>}
          {adminMode && <div className="form-foot">Not an admin? <button data-testid="admin-back-home" onClick={() => setRole("client")}>Login as client or artist</button></div>}
          {creatorMode && <div className="form-foot">Not a creator? <button data-testid="creator-back-home" onClick={() => setRole("client")}>Login as client or artist</button></div>}
        </div>
      </section>
    </main>
  );
}
